import { CookieAttributes, CookiesStatic } from 'js-cookie';

type Cookie = CookiesStatic<never>;

type CookieValue = {
  value: string;
  createdOn: Date;
  options?: CookieAttributes;
};

type CookieState = {
  [key: string]: CookieValue | undefined;
};

const state: CookieState = {};

/**
 * **WARNING**: This is a PARTIAL implementation of js-cookie interface.
 * It should be used only if cookies are not enabled. Also note that these
 * cookies will be destroyed as the page is closed.
 */
export interface InMemoryCookiesType {
  get: Cookie['get'];
  set: Cookie['set'];
  remove: Cookie['remove'];
}

function remove(name: string) {
  delete state[name];
}

function get(name: string): string | undefined {
  const cookie = state[name];

  if (!cookie) return undefined;

  const { value, options, createdOn } = cookie;

  let expirationDate: Date | undefined = undefined;

  if (options?.expires) {
    switch (typeof options.expires) {
      case 'number': {
        const day = 60 * 60 * 24 * 1000;
        expirationDate = new Date(createdOn.getTime() + options.expires * day);
        break;
      }
      case 'object':
        if (options.expires instanceof Date) {
          expirationDate = options.expires;
        }
        break;
      default:
        break;
    }
  }

  if (expirationDate && Date.now() < expirationDate.getTime()) {
    // cookie is valid
    return value;
  } else {
    // cookie expired
    remove(name);
  }

  return undefined;
}

/**
 * **WARNING**: This is a PARTIAL implementation of js-cookie interface.
 * It should be used only if cookies are not enabled. Also note that these
 * cookies will be destroyed as the page is closed.
 */
export const InMemoryCookies: InMemoryCookiesType = {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  get(name) {
    if (typeof name === 'undefined') {
      const cookies: { [key: string]: string } = {};

      for (const name in state) {
        const value = get(name);
        if (Object.prototype.hasOwnProperty.call(state, name) && value) {
          cookies[name] = value;
        }
      }

      return cookies;
    }

    return get(name);
  },
  set(name, value, options) {
    state[name] = {
      value,
      options,
      createdOn: new Date(),
    };
    return state[name]?.value;
  },
  remove,
};
